import { compressImage } from '@helper/imageCompression';

interface UploadOptions {
	maxSizeInBytes?: number;
	quality?: number;
	maxIterations?: number;
}

/**
 * Upload a blob to Azure Block Blob storage using one base pre-signed URL.
 * Each 5 MB slice is PUT with `comp=block&blockid=...`; then `comp=blocklist` commits.
 */
export async function uploadFileToGcp(blob: Blob, preSignedURL: string, contentType?: string): Promise<Response> {
	const type = contentType ?? blob.type ?? 'application/octet-stream';

	const headers: Record<string, string> = {
		'Content-Type': type,
	};

	// If the backend returns an Azure Blob Storage URL, we must include the Azure-specific header.
	// If it returns a GCP URL, we omit it to avoid CORS errors.
	if (preSignedURL.includes('.blob.core.windows.net')) {
		headers['x-ms-blob-type'] = 'BlockBlob';
	}

	const response = await fetch(preSignedURL, {
		method: 'PUT',
		body: blob,
		headers,
	});

	if (!response.ok) {
		let detail = response.statusText;
		try {
			const text = await response.text();
			detail = text.slice(0, 200);
		} catch {
			// ignore
		}
		throw new Error(`Upload failed: HTTP ${response.status} — ${detail}`);
	}

	return response;
}

export const uploadCompressFile = async (
	file: File,
	preSignedURL: string,
	options: UploadOptions = {}
): Promise<Response | undefined> => {
	const { maxSizeInBytes = 2 * 1024 * 1024, quality = 0.8, maxIterations = 10 } = options;

	let fileToUpload = file;

	if (file.type.startsWith('image/')) {
		try {
			fileToUpload = await compressImage(file, { maxSizeInBytes, quality, maxIterations });
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error compressing image:', error);
		}
	}

	try {
		return await uploadFileToGcp(fileToUpload, preSignedURL, fileToUpload.type);
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('file upload', error);
		throw error;
	}
};
